import { apiRequest } from '../apiRequest';

export const organisationsSkillsAPI = {
  getMatrixTechFacets: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/matrix/tech-facets`, {
      method: 'GET',
    });
    return data.techs ?? [];
  },
  /** Matrix: repos, projects, streams, members matching stored tech stack items. */ matrixSearchByTech:
    async (orgId, { q, match = 'contains' }: { q?: string; match?: string } = {}) => {
      const params = new URLSearchParams();
      const trimmed = q != null ? String(q).trim() : '';
      if (trimmed) params.set('q', trimmed);
      params.set('match', match === 'equals' ? 'equals' : 'contains');
      const qs = params.toString();
      const data = await apiRequest<any>(
        `/api/organisations/${orgId}/matrix/by-tech${qs ? `?${qs}` : ''}`,
        { method: 'GET' }
      );
      return {
        query: data.query ?? '',
        match: data.match ?? 'contains',
        repositories: data.repositories ?? [],
        projects: data.projects ?? [],
        streams: data.streams ?? [],
        members: data.members ?? [],
        truncated: data.truncated ?? { repositories: false, members: false },
      };
    },
  /** Skill profiles: filter chips (distinct skill names per tag type, with member counts). */ getSkillFacets:
    async (orgId) => {
      const data = await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/facets`, {
        method: 'GET',
      });
      if (data?.facets && typeof data.facets === 'object' && !Array.isArray(data.facets)) {
        return data.facets;
      }
      // Some callers may already receive the unwrapped facet map.
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const values = Object.values(data);
        if (values.some((v) => Array.isArray(v))) {
          return data;
        }
      }
      return {};
    },
  /** Skill profiles: person-first search (semantic when a free-text query is present, else structured). */ searchSkillProfiles:
    async (
      orgId,
      {
        query = '',
        tags = [],
        minProficiency,
        mode = 'auto',
        limit,
      }: {
        query?: string;
        tags?: string[];
        mode?: string;
        minProficiency?: string;
        limit?: number;
      } = {},
    ) => {
      const body: {
        query: string;
        tags: string[];
        mode: string;
        minProficiency?: string;
        limit?: number;
      } = { query: String(query || ''), tags: Array.isArray(tags) ? tags : [], mode };
      const prof = minProficiency != null ? String(minProficiency).trim() : '';
      if (prof) body.minProficiency = prof;
      if (limit != null) body.limit = limit;
      const data = await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/search`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return {
        query: data.query ?? '',
        tags: data.tags ?? [],
        members: data.members ?? [],
        truncated: data.truncated ?? false,
        mode: data.mode ?? 'structured',
      };
    },
  /** Skill profiles: lightweight list for the org (member card grid). */   listSkillProfiles: async (
    orgId,
    { status, topTags }: { status?: string; topTags?: number } = {}
  ) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (topTags != null) params.set('topTags', String(topTags));
    const qs = params.toString();
    const data = await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
    return data.profiles ?? [];
  },
  /** Skill profiles: full profile for a single member. */ getSkillProfile: async (
    orgId,
    userId
  ) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/${userId}`, {
      method: 'GET',
    });
    return data.profile ?? null;
  },
  /** Skill profiles: the authenticated member's own profile. */ getMySkillProfile: async (
    orgId
  ) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/me`, {
      method: 'GET',
    });
    return data.profile ?? null;
  },
  /** Skill profiles: enqueue (re)generation for one member. */ generateSkillProfile: async (
    orgId,
    userId,
    { force = false } = {}
  ) => {
    return await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/${userId}/generate`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  },
  /** Skill profiles: enqueue (re)generation for the whole organisation. */ generateAllSkillProfiles:
    async (orgId, { force = false } = {}) => {
      return await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/generate-all`, {
        method: 'POST',
        body: JSON.stringify({ force }),
      });
    },
  /** Skill profiles: member edits their own free-text summary. */ updateMySkillSummary: async (
    orgId,
    summary
  ) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/me/summary`, {
      method: 'PATCH',
      body: JSON.stringify({ summary }),
    });
    return data.profile ?? null;
  },
  /** Skill profiles: member confirms/rejects/adjusts a single derived tag. */ reviewMySkillTag:
    async (orgId, tagId, review) => {
      const data = await apiRequest<any>(
        `/api/organisations/${orgId}/skill-profiles/me/tags/${tagId}/review`,
        {
          method: 'PATCH',
          body: JSON.stringify({ review }),
        }
      );
      return data.tag ?? null;
    },
  /** Skill profiles: list the member's self-declared skills. */ listMySkillDeclarations: async (
    orgId
  ) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/me/declarations`, {
      method: 'GET',
    });
    return data.declarations ?? [];
  },
  /** Skill profiles: create or update a self-declared skill. */ upsertMySkillDeclaration: async (
    orgId,
    declaration
  ) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/skill-profiles/me/declarations`, {
      method: 'PUT',
      body: JSON.stringify(declaration || {}),
    });
    return data.declaration ?? null;
  },
  /** Skill profiles: delete a self-declared skill. */ deleteMySkillDeclaration: async (
    orgId,
    declarationId
  ) => {
    return await apiRequest<any>(
      `/api/organisations/${orgId}/skill-profiles/me/declarations/${declarationId}`,
      {
        method: 'DELETE',
      }
    );
  },
  /** Inbound GitHub webhook deliveries for this org (correlate with GitHub → Webhooks → Recent deliveries). */
};
